import React from 'react';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  Home,
  Gamepad2,
  Users,
  Trophy,
  Calendar,
  Settings,
  BarChart3,
  Gift,
  Newspaper,
  MessageSquare,
  CreditCard,
  Menu,
  X,
  Star,
  Image as ImageIcon,
  DollarSign,
  Target
} from 'lucide-react';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';

const SidebarContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 280px;
  height: 100vh;
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
  border-left: 2px solid #2d2d44;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #2d2d44;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #00d4ff;
  font-size: 20px;
  font-weight: bold;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
  
  &:hover {
    background: #2d2d44;
  }
`;

const Navigation = styled.nav`
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 20px;
  color: #b8b8d1;
  text-decoration: none;
  transition: all 0.3s ease;
  border-right: 3px solid transparent;
  position: relative;
  
  &:hover {
    background: linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, transparent 100%);
    color: #00d4ff;
    border-right-color: #00d4ff;
  }
  
  &.active {
    background: linear-gradient(90deg, rgba(0, 212, 255, 0.2) 0%, transparent 100%);
    color: #00d4ff;
    border-right-color: #00d4ff;
    font-weight: 600;
  }
  
  &.active::before {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, #00d4ff 0%, #4ecdc4 100%);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const SidebarFooter = styled.div`
  padding: 20px;
  border-top: 1px solid #2d2d44;
  color: #6c6c8a;
  font-size: 12px;
  text-align: center;
`;

const menuItems = [
  { path: '/', icon: Home, label: PERSIAN_TEXTS['menu.dashboard'] },
  { path: '/games', icon: Gamepad2, label: PERSIAN_TEXTS['menu.games'] },
  { path: '/players', icon: Users, label: PERSIAN_TEXTS['menu.players'] },
  { path: '/matches', icon: Calendar, label: PERSIAN_TEXTS['menu.matches'] },
  { path: '/tournaments', icon: Trophy, label: PERSIAN_TEXTS['menu.tournaments'] },
  { path: '/ranks', icon: Star, label: 'رتبه‌ها' },
  { path: '/sliders', icon: ImageIcon, label: 'اسلایدرها' },
  { path: '/finance', icon: CreditCard, label: PERSIAN_TEXTS['menu.finance'] },
  { path: '/admin-payouts', icon: DollarSign, label: 'پرداخت‌های ادمین' },
  { path: '/prize-structures', icon: Target, label: 'ساختارهای جایزه' },
  { path: '/achievements', icon: Gift, label: PERSIAN_TEXTS['menu.achievements'] },
  { path: '/news', icon: Newspaper, label: PERSIAN_TEXTS['menu.news'] },
  { path: '/support-tickets', icon: MessageSquare, label: PERSIAN_TEXTS['menu.supportTickets'] },
  { path: '/settings', icon: Settings, label: 'تنظیمات' },
];

const Sidebar = ({ isOpen, onToggle }) => {
  const isMobile = useMediaQuery('(max-width:768px)');

  const SidebarContent = (
    <>
      <SidebarHeader>
        <Logo>
          <Trophy size={24} />
          <span>تورنومنت ادمین</span>
        </Logo>
        <CloseButton onClick={onToggle}>
          <X size={20} />
        </CloseButton>
      </SidebarHeader>
      
      <Navigation>
        {menuItems.map((item) => (
          <NavItem key={item.path} to={item.path}>
            <IconWrapper>
              <item.icon size={20} />
            </IconWrapper>
            {item.label}
          </NavItem>
        ))}
      </Navigation>
      
      <SidebarFooter>
        <div>پنل مدیریت تورنومنت</div>
        <div>نسخه 1.0.0</div>
      </SidebarFooter>
    </>
  );

  return (
    <>
      {!isMobile && (
        <SidebarContainer
          isOpen={isOpen}
          initial={false}
          animate={{ x: isOpen ? 0 : 280 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {SidebarContent}
        </SidebarContainer>
      )}

      {isMobile && (
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={onToggle}
          ModalProps={{ keepMounted: true, disableScrollLock: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: '85vw',
              maxWidth: 360,
              background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
              borderLeft: '2px solid #2d2d44',
            },
          }}
        >
          {SidebarContent}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
